import React, { useState, useEffect } from "react";
import { useParams, Navigate, Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { ArrowLeft } from "lucide-react";
import { Helmet } from "react-helmet-async";

// PAGE SLUG → SITE CONFIG KEY MAP
// These pages are now controlled by the Tech Team dashboard (Site Content tab)
const SITE_CONFIG_PAGES = {
  faq: {
    titleKey: "faq_heading",
    defaultTitle: "Frequently Asked Questions",
    seoTitleKey: "faq_seo_title",
    seoDescKey: "faq_seo_description",
    renderContent: (SC) => {
      const items = [];
      for (let i = 1; i <= 10; i++) {
        const q = SC[`faq_${i}_question`];
        const a = SC[`faq_${i}_answer`];
        if (q && a) items.push({ q, a });
      }
      return items.length === 0 ? (
        <p className="text-[#94A3B8]">
          {SC.faq_subheading || "FAQ content is being updated. Please check back soon."}
        </p>
      ) : (
        <div className="space-y-4">
          {SC.faq_subheading && (
            <p className="text-[#94A3B8] mb-8">{SC.faq_subheading}</p>
          )}
          {items.map((item, idx) => (
            <FaqItem key={idx} q={item.q} a={item.a} />
          ))}
        </div>
      );
    },
  },
  contact: {
    titleKey: "contact_heading",
    defaultTitle: "Get In Touch With Us",
    seoTitleKey: "contact_seo_title",
    seoDescKey: "contact_seo_description",
    renderContent: (SC) => (
      <div className="space-y-6">
        {SC.contact_subheading && (
          <p className="text-[#94A3B8] text-lg">{SC.contact_subheading}</p>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {SC.contact_email && (
            <div className="p-5 rounded-lg border border-white/[0.06] bg-white/[0.02]">
              <p className="text-xs text-[#A1A1AA] uppercase tracking-widest mb-1">Email</p>
              <a href={`mailto:${SC.contact_email}`} className="text-[#00FF88] hover:underline font-medium">
                {SC.contact_email}
              </a>
            </div>
          )}
          {SC.contact_phone && (
            <div className="p-5 rounded-lg border border-white/[0.06] bg-white/[0.02]">
              <p className="text-xs text-[#A1A1AA] uppercase tracking-widest mb-1">Phone</p>
              <a href={`tel:${SC.contact_phone}`} className="text-[#00FF88] hover:underline font-medium">
                {SC.contact_phone}
              </a>
            </div>
          )}
          {SC.contact_whatsapp && (
            <div className="p-5 rounded-lg border border-white/[0.06] bg-white/[0.02]">
              <p className="text-xs text-[#A1A1AA] uppercase tracking-widest mb-1">WhatsApp</p>
              <a href={SC.contact_whatsapp} target="_blank" rel="noopener noreferrer" className="text-[#00FF88] hover:underline font-medium">
                Chat with us on WhatsApp →
              </a>
            </div>
          )}
          {SC.contact_telegram && (
            <div className="p-5 rounded-lg border border-white/[0.06] bg-white/[0.02]">
              <p className="text-xs text-[#A1A1AA] uppercase tracking-widest mb-1">Telegram</p>
              <a href={SC.contact_telegram} target="_blank" rel="noopener noreferrer" className="text-[#00FF88] hover:underline font-medium">
                Join our Telegram →
              </a>
            </div>
          )}
        </div>
        {SC.contact_address && (
          <div className="p-5 rounded-lg border border-white/[0.06] bg-white/[0.02]">
            <p className="text-xs text-[#A1A1AA] uppercase tracking-widest mb-1">Office Address</p>
            <p className="text-white whitespace-pre-wrap">{SC.contact_address}</p>
          </div>
        )}
        {SC.contact_map_url && (
          <div className="rounded-lg overflow-hidden border border-white/[0.06]">
            <iframe
              src={SC.contact_map_url}
              width="100%"
              height="300"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
              title="Location Map"
            />
          </div>
        )}
      </div>
    ),
  },
  privacy: {
    titleKey: "privacy_title",
    defaultTitle: "Privacy Policy",
    seoTitleKey: "privacy_seo_title",
    seoDescKey: "privacy_seo_description",
    renderContent: (SC) => (
      <div>
        {SC.privacy_last_updated && (
          <p className="text-xs text-[#A1A1AA] mb-6">Last Updated: {SC.privacy_last_updated}</p>
        )}
        <div className="text-[#CBD5E1] leading-relaxed whitespace-pre-wrap font-plex">
          {SC.privacy_content || "Privacy policy content is being updated."}
        </div>
      </div>
    ),
  },
  terms: {
    titleKey: "terms_title",
    defaultTitle: "Terms & Conditions",
    seoTitleKey: "terms_seo_title",
    seoDescKey: "terms_seo_description",
    renderContent: (SC) => (
      <div>
        {SC.terms_last_updated && (
          <p className="text-xs text-[#A1A1AA] mb-6">Last Updated: {SC.terms_last_updated}</p>
        )}
        <div className="text-[#CBD5E1] leading-relaxed whitespace-pre-wrap font-plex">
          {SC.terms_content || "Terms and conditions content is being updated."}
        </div>
      </div>
    ),
  },
  refunds: {
    titleKey: "refunds_title",
    defaultTitle: "Refund & Cancellation Policy",
    seoTitleKey: "refunds_seo_title",
    seoDescKey: "refunds_seo_description",
    renderContent: (SC) => (
      <div className="text-[#CBD5E1] leading-relaxed whitespace-pre-wrap font-plex">
        {SC.refunds_content || "Refund policy content is being updated."}
      </div>
    ),
  },
};

// FAQ accordion for contact page
const FaqItem = ({ q, a }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-white/[0.06] rounded-lg overflow-hidden">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between p-5 text-left hover:bg-white/[0.02]">
        <span className="text-base font-semibold text-white font-outfit pr-4">{q}</span>
        <span className={`text-[#00FF88] transition-transform duration-200 ${open ? "rotate-180" : ""}`}>▼</span>
      </button>
      {open && (
        <div className="px-5 pb-5 text-sm text-[#CBD5E1] font-plex leading-relaxed border-t border-white/[0.04]">
          {a}
        </div>
      )}
    </div>
  );
};

const DynamicPage = ({ slug: defaultSlug }) => {
  const { slug: paramSlug } = useParams();
  const slug = defaultSlug || paramSlug;
  const [siteConfig, setSiteConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/site-config`);
        setSiteConfig(res.data || {});
      } catch {
        setSiteConfig({});
      } finally {
        setLoading(false);
      }
    };
    if (slug) fetchData();
  }, [slug]);

  // If slug doesn't match any known page, redirect home
  if (!slug || (!defaultSlug && !SITE_CONFIG_PAGES[slug])) {
    return <Navigate to="/" replace />;
  }

  const pageConfig = SITE_CONFIG_PAGES[slug];

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0E17] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#00F0FF] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const SC = siteConfig || {};
  const pageTitle = SC[pageConfig?.titleKey] || pageConfig?.defaultTitle || slug;
  const seoTitle = SC[pageConfig?.seoTitleKey] || `${pageTitle} | Jamadhan`;
  const seoDesc = SC[pageConfig?.seoDescKey] || "";

  return (
    <div className="min-h-screen bg-[#0A0E17] flex flex-col">
      <Helmet>
        <title>{seoTitle}</title>
        <meta name="description" content={seoDesc} />
      </Helmet>

      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? "bg-[#050505]/90 backdrop-blur-xl border-b border-white/5" : "bg-transparent"}`}>
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5 group">
            <div className="w-9 h-9 rounded overflow-hidden shadow-[0_0_20px_rgba(0,255,136,0.2)] border border-[#00FF88]/20 group-hover:border-[#00FF88]/60 transition-all duration-300">
              <img src="/jamadhan-logo.png" alt="Jamadhan" className="w-full h-full object-cover" />
            </div>
            <span className="text-lg font-bold text-white font-outfit tracking-tight group-hover:text-[#00FF88] transition-colors">JAMADHAN</span>
          </Link>
          <div className="flex items-center gap-4">
            <Link to="/" className="text-sm text-[#A1A1AA] font-medium hover:text-[#00FF88] transition-colors flex items-center gap-1.5">
              <ArrowLeft className="w-4 h-4" /> Back to Home
            </Link>
          </div>
        </div>
      </nav>

      <main className="flex-1 relative z-10 pt-32 pb-20">
        <div className="absolute inset-0 bg-[linear-gradient(rgba(0,255,136,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(0,240,255,0.02)_1px,transparent_1px)] bg-[size:40px_40px] pointer-events-none opacity-50" />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 relative">
          <div className="bg-[#131B2C]/50 backdrop-blur-sm border border-white/[0.06] rounded-2xl p-8 md:p-12 shadow-xl">
            <h1 className="text-3xl md:text-5xl font-bold text-white font-outfit mb-8 pb-8 border-b border-white/10">
              {pageTitle}
            </h1>
            {pageConfig?.renderContent ? pageConfig.renderContent(SC) : (
              <p className="text-[#94A3B8]">Content coming soon.</p>
            )}
          </div>
        </div>
      </main>

      <footer className="border-t border-white/5 py-12 bg-[#050505] relative z-10">
        <div className="max-w-7xl mx-auto px-6 text-center text-[#52525B] text-sm font-plex">
          <p>{SC.footer_copyright || `© ${new Date().getFullYear()} Jamadhan. All rights reserved.`}</p>
        </div>
      </footer>
    </div>
  );
};

export default DynamicPage;
